import { styled } from '@linaria/react';
import { Button } from '../../components/Button';
import { EntityImage } from '../../components/EntityImage';
import { fonts, layout, primitives, semantic, size, space, textStyle } from '../../theme/tokens';

const tileSize = '152px';

export const RecommendationsWrapper = styled.div`
  width: calc(100% + var(--carousel-edge-inset, 0));
  margin: 0 auto;
  margin-left: calc(-1 * var(--carousel-edge-inset, 0));
  padding-left: var(--carousel-edge-inset, 0);
  overflow: hidden;
`;

export const RecommendationsTitle = styled.h2`
  font-family: ${fonts.heading};
  font-size: ${primitives.typography['scale-text']['scale-text-900']};
  font-weight: ${fonts.weight.normal};
  letter-spacing: ${primitives.typography.tracking.tight};
  line-height: ${primitives.typography.lineHeight.tight};
  color: ${semantic.text.primary};
  margin: 0;
`;

export const CenteredGrid = styled.div<{
  $columns: number;
  $mobileColumns: number;
  $rowGap?: string;
}>`
  --cols: ${({ $mobileColumns }) => $mobileColumns};
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ $rowGap }) => $rowGap ?? space['space-600']} ${space['space-600']};
  margin-bottom: ${space['space-2000']};

  > * {
    flex: 0 0 calc((100% - (var(--cols) - 1) * ${space['space-600']}) / var(--cols));
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    --cols: ${({ $columns }) => $columns};
  }
`;

export const GridStack = styled.div<{ $rowGap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${space['space-500']};
  width: 100%;
  margin-bottom: ${space['space-2000']};

  ${CenteredGrid} {
    margin-bottom: 0;
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    gap: ${({ $rowGap }) => $rowGap ?? space['space-600']};
  }
`;

export const FullWidthCell = styled.div<{ $columns: number }>`
  width: 100%;

  @media (min-width: ${layout.desktopBreakpoint}) {
    --cols: ${({ $columns }) => $columns};
    width: calc((100% - (var(--cols) - 1) * ${space['space-600']}) / var(--cols));
  }
`;

export const GridHeading = styled.p<{ $size?: 'base' | 'large' }>`
  margin: 0 0 ${({ $size }) => ($size === 'large' ? space['space-1000'] : space['space-600'])};
  text-align: center;
  font-family: ${fonts.heading};
  font-weight: ${fonts.weight.normal};
  font-size: ${fonts.size['2xl']};
  line-height: ${primitives.typography.lineHeight.tight};
  color: ${semantic.text.primary};

  @media (min-width: ${layout.desktopBreakpoint}) {
    font-size: ${({ $size }) => ($size === 'large' ? fonts.size['3xl'] : fonts.size['2xl'])};
  }
`;

export const GridHeadingSecondary = styled.span`
  color: ${semantic.text.secondary};
`;

export const RowSection = styled.section`
  border-top: ${size.stroke['stroke-default']} solid ${semantic.border.primary};
`;

export const RowHeading = styled.h2`
  margin: 0;
  padding: ${space['space-800']} ${space['space-600']} ${space['space-600']};
  text-align: center;
  font-family: ${textStyle.headline['size-lg'].fontFamily};
  font-size: ${textStyle.headline['size-lg'].fontSize};
  font-weight: ${textStyle.headline['size-lg'].fontWeight};
  line-height: ${textStyle.headline['size-lg'].lineHeight};
  letter-spacing: ${textStyle.headline['size-lg'].letterSpacing};
  color: ${semantic.text.primary};

  @media (min-width: ${layout.desktopBreakpoint}) {
    font-size: ${textStyle.headline['size-md'].fontSize};
  }
`;

export const TileRow = styled.ul`
  display: flex;
  gap: ${space['space-400']};
  margin: 0;
  padding: 0;
  list-style: none;
  margin-right: calc(-1 * var(--carousel-edge-inset, 0px));
  padding-right: var(--carousel-edge-inset, 0px);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &[data-dragging='true'] {
    scroll-snap-type: none;
    cursor: grabbing;
    user-select: none;
  }
`;

export const Tile = styled.li`
  flex: 0 0 ${tileSize};
  display: flex;
  flex-direction: column;
  gap: ${space['space-200']};
  scroll-snap-align: start;
  cursor: pointer;
`;

export const TileImage = styled.div`
  position: relative;
  width: ${tileSize};
  height: ${tileSize};
  border-radius: ${size.radius['radius-xs']};
  overflow: hidden;
  background: ${semantic.bg.surfaceSubtle};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${semantic.bg.surfaceSubtle};
    pointer-events: none;
  }
`;

export const TileImg = styled(EntityImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const TilePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semantic.text.tertiary};
  font-family: ${textStyle.body.sm.regular.fontFamily};
  font-size: ${textStyle.body.sm.regular.fontSize};
`;

export const TileAction = styled(Button)`
  position: absolute;
  z-index: 1;
  right: ${space['space-200']};
  bottom: ${space['space-200']};
  width: ${space['space-1000']};
  height: ${space['space-1000']};
  padding: 0;
  border-radius: ${size.radius['radius-full']};
`;

export const TileLabel = styled.p`
  margin: 0;
  text-align: center;
  font-family: ${textStyle.body.md.strong.fontFamily};
  font-size: ${textStyle.body.md.strong.fontSize};
  font-weight: ${textStyle.body.md.strong.fontWeight};
  line-height: ${textStyle.body.md.strong.lineHeight};
  letter-spacing: ${textStyle.body.md.strong.letterSpacing};
  color: ${semantic.text.primary};
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -webkit-box;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;
